import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { PageHeader } from "@/components/PageHeader";
import { useSimulation } from "@/features/simulations/hooks";
import { SimulationProgress } from "@/features/simulations/SimulationProgress";
import { SimulationAssumptions } from "@/features/simulations/SimulationAssumptions";
import { ResultKpis } from "@/features/simulations/ResultKpis";
import { BottleneckCard } from "@/features/simulations/BottleneckCard";
import { CandidateAsLineCard } from "@/features/simulations/CandidateAsLineCard";
import { getSimulationStatusLabel, formatDateTime } from "@/lib/display-labels";
import { InterpretRunButton } from "@/features/interpretations/InterpretRunButton";

export function SimulationDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const simulationQuery = useSimulation(id, 10_000);
  const { data, isLoading } = simulationQuery;
  const [showAsLine, setShowAsLine] = useState(false);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando…</p>;
  }
  // Erro real (500/timeout/etc.) nunca deve virar "não encontrada" (afirmação falsa).
  if (simulationQuery.isError) {
    return (
      <Notify.QueryError
        error={simulationQuery.error}
        onRetry={() => void simulationQuery.refetch()}
        title="Não foi possível carregar a simulação"
      />
    );
  }
  if (!data) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          Simulação não encontrada.
        </CardContent>
      </Card>
    );
  }

  return (
    <section>
      <PageHeader
        title={`Simulação ${data.id.slice(0, 8)}`}
        subtitle={`Status: ${getSimulationStatusLabel(data.status)}`}
        actions={
          <div className="flex gap-2">
            {data.status === "COMPLETED" ? <InterpretRunButton simulationId={data.id} /> : null}
            {data.results && data.lineId ? (
              <Button variant="outline" onClick={() => setShowAsLine((v) => !v)}>
                <Workflow className="h-4 w-4" />
                {showAsLine ? "Ocultar linha" : "Ver como linha"}
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => navigate("/simulations")}>
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
          </div>
        }
      />

      <SimulationProgress
        simulationId={data.id}
        status={data.status}
        onTerminal={() => void qc.invalidateQueries({ queryKey: ["simulations"] })}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Resultados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.results ? (
              <>
                <ResultKpis
                  results={data.results}
                  confidenceLevel={data.displayedConfidenceLevel ?? data.confidenceLevel}
                  confidenceBasis={data.confidenceBasis}
                  trackRecordSource={data.trackRecordSource}
                />
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
                    Gargalo
                  </h3>
                  <div className="mt-1">
                    <BottleneckCard bottleneck={data.results.bottleneck} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
                    Diagnóstico
                  </h3>
                  <p className="mt-1 text-sm">{data.results.diagnostic}</p>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {data.status === "FAILED"
                  ? `Falha: ${data.errorMessage ?? "Erro desconhecido."}`
                  : "Aguardando resultado…"}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Cenário</dt>
                <dd className="font-mono">{data.scenarioId.slice(0, 8)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Linha</dt>
                {/* LineId null em rodadas de cenário "linha nova". */}
                <dd className="font-mono">
                  {data.lineId ? data.lineId.slice(0, 8) : "Linha nova"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                  Enfileirada em
                </dt>
                <dd className="text-xs">{data.queuedAt ? formatDateTime(data.queuedAt) : "—"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                  Concluída em
                </dt>
                <dd className="text-xs">
                  {data.completedAt ? formatDateTime(data.completedAt) : "—"}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      {showAsLine && data.results && data.lineId ? (
        <div className="mt-4">
          <CandidateAsLineCard
            scenarioId={data.scenarioId}
            lineId={data.lineId}
            dataSources={data.dataSources}
            perMachine={data.results.perMachine}
          />
        </div>
      ) : null}

      {data.results && data.dataSources ? (
        <div className="mt-4">
          <SimulationAssumptions
            dataSources={data.dataSources}
            perMachine={data.results.perMachine}
          />
        </div>
      ) : null}
    </section>
  );
}
